/**
 * WARNING — not IME/Android-safe (currently unused; verify before wiring into any form).
 *
 * This hook owns the input's text by calling preventDefault() on `beforeinput` and applying the
 * edit itself. Composition events (`insertCompositionText`) are NOT cancelable per spec, so with
 * an IME — CJK input, dead keys, and most Android keyboards (predictive text composes every
 * word) — the browser inserts the text anyway, the hook inserts it again, and rewriting
 * input.value mid-composition desyncs the IME: doubled characters or a broken caret.
 *
 * Before adopting this control in a user-facing form, it needs a composition-aware protocol:
 * stand down entirely between compositionstart and compositionend (no preventDefault, no
 * onInsert, no DOM writes), then diff the committed DOM value against the last known state,
 * run it through the formatter in one pass, and restore the caret — validated on a real
 * Android device and a CJK IME.
 */
import {clamp} from "@2d-front-monorepo/core";
import {
	ClipboardEventHandler,
	DragEventHandler,
	FocusEventHandler,
	KeyboardEventHandler,
	MouseEventHandler,
	RefObject,
	useCallback,
	useEffect,
	useRef
} from "react";

export type InsertFn = (text: string) => void;

export type SetValueFn = (
	value: string,
	caretStart: number,
	caretEnd?: number
) => void;

export type SetCaretFn = (start: number, end?: number) => void

export type OnInsertFn = (
	params: Readonly<{
		char: string;
		insert: InsertFn;
		setCaret: SetCaretFn;
		setValue: SetValueFn;
		value: string;
		caret: { left: number; right: number };
	}>
) => void;

export type FormatFn = (value: string) => {
	formatted: string;
	mapping: number[];
}

export interface FormattedInputOptions {
	value?: string;
	onChange?: (value: string, formattedValue: string) => void;
	onBlur?: (value: string) => string;
	onInsert?: OnInsertFn;
	liveUpdate?: boolean;
	format?: FormatFn;
}

interface InternalInputState extends Required<Pick<FormattedInputOptions, "onChange" | "value" | "onInsert" | "format" | "onBlur">> {
	caretStart: number
	caretEnd: number
}

function updateDom(input: HTMLInputElement, state: InternalInputState) {
	const caretLeft = Math.min(state.caretStart, state.caretEnd);
	const caretRight = Math.max(state.caretStart, state.caretEnd);

	const {formatted, mapping} = state.format(state.value);

	input.value = formatted;

	if (input === document.activeElement) {
		if (caretLeft === caretRight) {
			input.selectionStart = caretLeft === 0 ? mapping[0] : mapping[caretLeft] + 1;
		} else {
			input.selectionStart = mapping[caretLeft + 1];
		}

		if (caretLeft === caretRight) {
			input.selectionEnd = input.selectionStart;
		} else {
			input.selectionEnd = mapping[caretRight] + 1;
		}
	}
}

function getCursorPosition(mapping: number[], position: number) {
	let cursor = 0;
	while (
		Math.abs(mapping[cursor] + (!cursor ? 0 : 1) - position) >
		Math.abs((mapping[cursor + 1] ?? Infinity) + 1 - position)
		) {
		cursor++;
	}

	return cursor;
}

function defaultNullFn() {
	return null;
}

function defaultIdentityFn(value: string) {
	return value;
}

function defaultFormat(value: string) {
	return {
		formatted: value,
		mapping: [0, ...value.split("").map((_, i) => i)]
	};
}

export interface FormattedInputProps {
	onKeyDown: KeyboardEventHandler<HTMLInputElement>;
	onFocus: FocusEventHandler<HTMLInputElement>;
	onBlur: FocusEventHandler<HTMLInputElement>;
	onMouseDown: MouseEventHandler<HTMLInputElement>;
	onCopy: ClipboardEventHandler<HTMLInputElement>;
	onCut: ClipboardEventHandler<HTMLInputElement>;
	onDragStart: DragEventHandler<HTMLInputElement>;
}

export interface FormattedInputResult {
	props: FormattedInputProps;
	setCaret: SetCaretFn;
	insert: InsertFn;
	setValue: SetValueFn;
}

export const useFormattedInput = function (options: FormattedInputOptions | undefined, inputRef: RefObject<HTMLInputElement | null>): FormattedInputResult {
	const {
		value = "",
		onChange = defaultNullFn,
		onBlur: blurHandler = defaultIdentityFn,
		onInsert = defaultNullFn,
		liveUpdate = false,
		format = defaultFormat
	}: FormattedInputOptions = options ?? {};

	//https://react-formatted-input-hook.netlify.app/docs/api-reference#value
	// Use a ref to manage internal state to minimize re-renders
	const state = useRef<InternalInputState>({
		value,
		onInsert,
		onBlur: blurHandler,
		onChange,
		format,
		caretStart: 0,
		caretEnd: 0
	});

	state.current.onInsert = onInsert;
	state.current.onChange = onChange;
	state.current.onBlur = blurHandler;
	state.current.format = format;

	useEffect(() => {
		if (!inputRef.current) {
			return;
		}

		if (inputRef.current !== document.activeElement) {
			state.current.value = value;

			updateDom(inputRef.current, state.current);
		}
	}, [value]);

	const setCaret = useCallback<SetCaretFn>(
		function (start: number, end: number = start) {
			if (!inputRef.current) {
				return;
			}
			const currentState = state.current;
			currentState.caretStart = clamp(start, 0, currentState.value.length);
			currentState.caretEnd = clamp(end, 0, currentState.value.length);
			updateDom(inputRef.current, currentState);
		}, []);

	const insert = useCallback<InsertFn>(
		function (text) {
			const currentState = state.current;
			const caretLeft = Math.min(currentState.caretStart, currentState.caretEnd);
			const caretRight = Math.max(currentState.caretStart, currentState.caretEnd);

			currentState.value = currentState.value.slice(0, caretLeft) + text + currentState.value.slice(caretRight);
			setCaret(caretLeft + text.length);
		}, [setCaret]);

	const setValue = useCallback<SetValueFn>(
		function (value, caretStart, caretEnd) {
			state.current.value = value;
			setCaret(caretStart, caretEnd);
		}, [setCaret]);

	const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
		function (event) {
			if (!inputRef.current) {
				return;
			}

			const currentState = state.current;
			const caretLeft = Math.min(currentState.caretStart, currentState.caretEnd);
			const caretRight = Math.max(currentState.caretStart, currentState.caretEnd);
			// const caretLength = caretRight - caretLeft
			// const previousValue = state.current.value

			if (event.key === "a" && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
				setCaret(0, currentState.value.length);

			} else if (
				(event.key === "ArrowLeft" || event.key === "ArrowRight") && !event.metaKey && !event.altKey) {
				event.preventDefault();

				const delta = event.key === "ArrowLeft" ? -1 : 1;

				if (event.shiftKey) {
					setCaret(currentState.caretStart + delta, currentState.caretEnd);

				} else if (currentState.caretStart !== currentState.caretEnd) {
					setCaret(event.key === "ArrowLeft" ? caretLeft : caretRight);

				} else {
					setCaret(currentState.caretStart + delta);
				}
			} else if (event.key === "Home" || event.key === "ArrowUp" || event.key === "ArrowLeft" && (event.metaKey || event.altKey)) {
				event.preventDefault();

				if (event.shiftKey) {
					setCaret(0, caretRight);
				} else {
					setCaret(0);
				}

			} else if (event.key === "End" || event.key === "ArrowDown" || event.key === "ArrowRight" && (event.metaKey || event.altKey)) {
				event.preventDefault();

				if (event.shiftKey) {
					setCaret(currentState.value.length, caretLeft);
				} else {
					setCaret(currentState.value.length);
				}
			}
		}, [setCaret]);

	const onFocus = useCallback<FocusEventHandler<HTMLInputElement>>(function () {
		const currentInput = inputRef.current;
		if (!currentInput) {
			return
		}

		if (currentInput.selectionStart === 0 && currentInput.selectionEnd === currentInput.value.length) {
			setCaret(0, state.current.value.length);
		}
	}, [setCaret]);

	const onBlur = useCallback<FocusEventHandler<HTMLInputElement>>(function () {
		if (!inputRef.current) {
			return
		}

		const currentState = state.current;
		const previousValue = currentState.value;
		currentState.value = currentState.onBlur(previousValue);
		updateDom(inputRef.current, currentState);

		if (previousValue !== currentState.value || !liveUpdate) {
			currentState.onChange(currentState.value, inputRef.current.value);
		}
	}, [liveUpdate]);

	const onMouseDown = useCallback<MouseEventHandler<HTMLInputElement>>(function () {
		// Wait for the selection to be confirmed
		requestAnimationFrame(function () {
			const currentInput = inputRef.current;
			if (!currentInput) {
				return
			}

			const currentState = state.current;
			const {mapping} = currentState.format(currentState.value);

			// Only handle fixed caret, otherwise wait for mouse up
			if (currentInput.selectionStart === currentInput.selectionEnd) {
				setCaret(getCursorPosition(mapping, currentInput.selectionStart ?? 0));
			}
		})
	}, [setCaret]);

	// On mouse up: constrain selection
	useEffect(function () {
		const listener = function () {
			requestAnimationFrame(function () {
				const currentInput = inputRef.current;
				if (!currentInput || document.activeElement !== currentInput) return;

				const {mapping} = state.current.format(state.current.value);

				setCaret(
					getCursorPosition(mapping, currentInput.selectionStart ?? 0),
					getCursorPosition(mapping, currentInput.selectionEnd ?? 0)
				);
			});
		};

		document.addEventListener("mouseup", listener);
		return () => document.removeEventListener("mouseup", listener)
	}, [setCaret]);

	useEffect(function () {
		function listener(event: InputEvent) {
			event.preventDefault();

			const currentInput = inputRef.current;
			if (!currentInput) return;

			const currentState = state.current;
			const {mapping} = currentState.format(currentState.value);

			const caretLeft = getCursorPosition(mapping, currentInput.selectionStart ?? 0);
			const caretRight = getCursorPosition(mapping, currentInput.selectionEnd ?? 0);

			currentState.caretStart = caretLeft;
			currentState.caretEnd = caretRight;
			const caretLength = caretRight - caretLeft;
			const previousValue = currentState.value;

			const deleteSelection = () => {
				currentState.value = currentState.value.slice(0, caretLeft) + currentState.value.slice(caretRight);
				setCaret(caretLeft);
			};

			if (
				(event.inputType === "insertText" ||
					event.inputType === "insertReplacementText" ||
					event.inputType === "insertFromDrop" ||
					event.inputType === "insertFromPaste" ||
					event.inputType === "insertFromPasteAsQuotation" ||
					event.inputType === "insertCompositionText") &&
				event.data
			) {
				for (let i = 0; i < event.data.length; i++) {
					currentState.onInsert({
						value: currentState.value,
						char: event.data[i],
						insert,
						setCaret,
						caret: {
							left: currentState.caretStart,
							right: currentState.caretEnd
						},
						setValue
					})
				}

				if (event.inputType === "insertFromDrop") {
					setCaret(caretLeft, caretLeft + event.data.length)
				}

			} else if (
				event.inputType === "deleteContentBackward" ||
				event.inputType === "deleteByDrag" ||
				event.inputType === "deleteByCut" ||
				event.inputType === "deleteContent"
			) {
				if (caretLength !== 0) {
					deleteSelection();
				} else {
					currentState.value = currentState.value.slice(0, Math.max(0, caretLeft - 1)) + currentState.value.slice(caretRight);
					setCaret(caretLeft - 1);
				}

			} else if (event.inputType === "deleteContentForward") {
				if (caretLength !== 0) {
					deleteSelection();

				} else {
					currentState.value =
						currentState.value.slice(0, caretLeft) +
						currentState.value.slice(caretRight + 1);
					setCaret(caretLeft);
				}

			} else if (
				event.inputType === "deleteWordBackward" ||
				event.inputType === "deleteSoftLineBackward" ||
				event.inputType === "deleteHardLineBackward"
			) {
				if (caretLength !== 0) {
					deleteSelection();

				} else {
					currentState.value = currentState.value.slice(caretLeft);
					setCaret(0);
				}

			} else if (
				event.inputType === "deleteWordForward" ||
				event.inputType === "deleteSoftLineForward" ||
				event.inputType === "deleteHardLineForward"
			) {
				if (caretLength !== 0) {
					deleteSelection();

				} else {
					currentState.value = currentState.value.slice(0, caretLeft);
					setCaret(caretLeft);
				}

			} else if (event.inputType === "deleteEntireSoftLine") {
				if (caretLength !== 0) {
					deleteSelection();

				} else {
					currentState.value = "";
					setCaret(0);
				}
			}

			if (previousValue !== currentState.value && liveUpdate) {
				currentState.onChange(currentState.value, currentInput.value ?? "");
			}
		}

		inputRef.current?.addEventListener("beforeinput", listener);
		return () => inputRef.current?.removeEventListener("beforeinput", listener);
	}, [insert, liveUpdate, setCaret, setValue]);

	const onCopy = useCallback<ClipboardEventHandler<HTMLInputElement>>(
		function (event) {
			const currentState = state.current;
			const currentInput = inputRef.current;
			if (!currentInput) return;

			const {mapping} = currentState.format(currentState.value);
			const caretLeft = getCursorPosition(mapping, currentInput.selectionStart ?? 0);
			const caretRight = getCursorPosition(mapping, currentInput.selectionEnd ?? 0);

			event.clipboardData.setData("text/plain", currentState.value.slice(caretLeft, caretRight));
			event.preventDefault();
		}, []);

	const onCut = useCallback<ClipboardEventHandler<HTMLInputElement>>(
		function (event) {
			const currentState = state.current;
			const currentInput = inputRef.current;
			if (!currentInput) return;

			const {mapping} = currentState.format(currentState.value);
			const caretLeft = getCursorPosition(mapping, currentInput.selectionStart ?? 0);
			const caretRight = getCursorPosition(mapping, currentInput.selectionEnd ?? 0);

			event.clipboardData.setData("text/plain", currentState.value.slice(caretLeft, caretRight));
			event.preventDefault();

			currentState.value = currentState.value.slice(0, caretLeft) + currentState.value.slice(caretRight);
			setCaret(caretLeft);

			if (caretLeft !== caretRight && liveUpdate) {
				currentState.onChange(currentState.value, currentInput.value ?? "");
			}
		}, [setCaret, liveUpdate]);

	const onDragStart = useCallback<DragEventHandler<HTMLInputElement>>(
		function (event) {
			const currentState = state.current;
			const currentInput = inputRef.current;
			if (!currentInput) return;

			const {mapping} = currentState.format(currentState.value);
			const caretLeft = getCursorPosition(mapping, currentInput.selectionStart ?? 0);
			const caretRight = getCursorPosition(mapping, currentInput.selectionEnd ?? 0);

			event.dataTransfer.setData("text/plain", currentState.value.slice(caretLeft, caretRight));
		}, []);

	return {
		props: {
			onKeyDown,
			onFocus,
			onBlur,
			onMouseDown,
			onCopy,
			onCut,
			onDragStart
		},
		setCaret,
		insert,
		setValue
	}
};

// export type FormattedInput = ReturnType<typeof useFormattedInput>;
